import { TCurrencyCode } from '@shared/model/types/currency.type';

type TLocale = 'ru-RU' | 'en-US' | 'de-DE';

export interface FormatPriceOptions {
    minimumFractionDigits?: number;
    maximumFractionDigits?: number;
    withCurrency?: boolean;
    currencyCode?: TCurrencyCode;
    currencySign?: string;
}

/**
 * Форматирует числовое значение с учётом локали и настроек.
 *
 * Возможности:
 * – автоматический выбор локали на основе глобального LOCALE (`ru` / `en`);
 * – вывод с или без знака валюты;
 * – управление количеством знаков после запятой.
 *
 * @param value Числовое значение, которое требуется отформатировать.
 * @param options Настройки форматирования.
 * @param options.minimumFractionDigits Минимальное количество знаков после запятой.
 * @param options.maximumFractionDigits Максимальное количество знаков после запятой. По умолчанию `0`.
 * @param options.withCurrency Выводить ли знак валюты. По умолчанию `false`.
 * @param options.currencyCode Необязательный код валюты с бэка (`RUB` / `USD` / `EUR`).
 * Если передан, используется вместо валюты по умолчанию для текущей локали.
 * @param options.currencySign Необязательный знак валюты (например, `₽`, `$`, `€`).
 *
 * @returns Локализованная строка, например `"1 990"`, `"1 990 ₽"` или `"$1,990"`.
 *
 * @example
 * formatPrice(1990);
 * // "1 990"
 *
 * @example
 * formatPrice(1990, { withCurrency: true });
 * // "1 990 ₽"
 *
 * @example
 * formatPrice(49.9, { maximumFractionDigits: 2, withCurrency: true, currencyCode: 'USD' });
 * // "$49.9"
 *
 * @example
 * formatPrice(1990, { currencySign: '$' });
 * // "1 990,00 $" (для `ru-RU`) / "$1,990.00" (для `en-US`)
 */
export const formatPrice = (
    value: number,
    {
        minimumFractionDigits,
        maximumFractionDigits = 0,
        withCurrency = false,
        currencyCode = null,
        currencySign,
    }: FormatPriceOptions = {}
): string => {
    const getCurrentOptions = (
        code: TCurrencyCode | null
    ): { locale: TLocale; currency: TCurrencyCode } => {
        // @ts-ignore
        if (LOCALE === 'ru') {
            return {
                locale: 'ru-RU',
                currency: code ?? 'RUB',
            };
        }
        // @ts-ignore
        if (LOCALE === 'en') {
            return {
                locale: 'en-US',
                currency: code ?? 'USD',
            };
        }
        // @ts-ignore
        if (LOCALE === 'de') {
            return {
                locale: 'de-DE',
                currency: code ?? 'EUR',
            };
        }
        return {
            locale: 'ru-RU',
            currency: 'RUB',
        };
    };

    const { locale, currency: localeCurrencyCode } = getCurrentOptions(currencyCode);
    const shouldShowCurrency = withCurrency || Boolean(currencySign);

    const formatter = new Intl.NumberFormat(locale, {
        style: shouldShowCurrency ? 'currency' : 'decimal',
        currency: shouldShowCurrency ? localeCurrencyCode : undefined,
        minimumFractionDigits,
        maximumFractionDigits,
    });

    if (value === null || value === undefined) return '0';

    if (!shouldShowCurrency || !currencySign) {
        return formatter.format(value);
    }

    return formatter
        .formatToParts(value)
        .map((part) => (part.type === 'currency' ? currencySign : part.value))
        .join('');
};
